"use client";

import dynamic from "next/dynamic";
import Script from "next/script";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState, useSyncExternalStore } from "react";
import type { ClientAnalyticsConfig } from "./config";
import { classifyLink, type AnalyticsItem, type EventMap, type ShareMethod } from "./events";
import { getServerState, getState, init, isTracking, pageView, subscribe, track } from "./runtime";

// Kök düzende (atlama bağlantısından hemen sonra) bir kez: onay durumu, sayfa görüntüleme izleyicisi, tıklama
// olayları, onaydan sonra gtag.js ve gerektiğinde çerez bandı. Sunucuda hiçbir şey çizmez (bant hidrasyondan sonra;
// sabit konumda olduğu için yerleşim kayması yok). Analitik kapalıysa (ölçüm kimliği yok) kök düzen bunu hiç eklemez.

// Bant ve tercih penceresi ayrı parça: onay verilmiş/reddedilmiş ziyaretçiye indirilmez
const ConsentUI = dynamic(() => import("./consent-ui"), { ssr: false });

export function AnalyticsRoot({ config }: { config: ClientAnalyticsConfig }) {
  const state = useSyncExternalStore(subscribe, getState, getServerState);
  // Bir kez gerekince (bant ya da pencere) bağlı kalır: "Tercihiniz kaydedildi" durum mesajı okunabilsin
  const [uiNeeded, setUiNeeded] = useState(false);
  const needed = state.ready && (state.consent === "unknown" || state.preferencesOpen);
  if (needed && !uiNeeded) setUiNeeded(true);

  const configKey = JSON.stringify(config);
  useEffect(() => {
    init(JSON.parse(configKey) as ClientAnalyticsConfig);
  }, [configKey]);
  useClickEvents(config.agencyPanelUrl);
  const scriptReady = useAfterLoad(state.loadScript);

  return (
    <>
      <Suspense fallback={null}>
        <PageViewTracker />
      </Suspense>
      {scriptReady && config.measurementId && !config.dryRun && (
        // Onaydan sonra; sayfa yüklendikten (load) sonra ilk boş anda, en geç 1,5 sn içinde (LCP ve etkileşimi etkilemez).
        // Komutlar (consent, config, page_view …) o zamana dek dataLayer'da bekler.
        <Script id="gtag-js" src={`https://www.googletagmanager.com/gtag/js?id=${config.measurementId}`} strategy="afterInteractive" />
      )}
      {uiNeeded && <ConsentUI config={config} state={state} />}
    </>
  );
}

/**
 * gtag.js ne zaman eklensin: sayfanın load olayından sonra, tarayıcının ilk boş anında (requestIdleCallback, en fazla
 * 1,5 sn bekleyerek). next/script "lazyOnload" süresiz boş an beklediği için (sürekli animasyonlu sayfada) kullanılmadı.
 */
function useAfterLoad(active: boolean): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!active || ready) return;
    let idle = 0;
    let timer = 0;
    const schedule = () => {
      if (typeof window.requestIdleCallback === "function") idle = window.requestIdleCallback(() => setReady(true), { timeout: 1500 });
      else timer = window.setTimeout(() => setReady(true), 200);
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => {
      window.removeEventListener("load", schedule);
      if (idle) window.cancelIdleCallback?.(idle);
      window.clearTimeout(timer);
    };
  }, [active, ready]);
  return ready;
}

/** İstemci gezintisi başına bir sayfa görüntüleme (yol ya da sorgu değişince); aynı adres tekrar sayılmaz (runtime.pageView) */
function PageViewTracker() {
  const pathname = usePathname();
  const query = useSearchParams().toString();
  useEffect(() => {
    pageView();
  }, [pathname, query]);
  return null;
}

const EXPLICIT_EVENTS = new Set<keyof EventMap>(["find_agency_click", "select_departure"]);
const SHARE_METHODS = new Set<ShareMethod>(["whatsapp", "facebook", "x", "linkedin", "telegram", "email", "copy"]);

function parseJson<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

/**
 * Belge düzeyinde tek dinleyici (sunucu bileşenlerindeki bağlantılar da): data-ga-event (açık olay), paylaşım düğmeleri
 * ([data-share] içinde data-network / data-share-method), tur kartı (data-ga-item → select_item), tel:/mailto:/WhatsApp/
 * harita (contact_click) ve acente paneli (agency_login_click). Konum: en yakın data-ga-area.
 */
function useClickEvents(agencyPanelUrl: string) {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.type === "auxclick" && e.button !== 1) return;
      if (!isTracking()) return;
      const el = (e.target as Element | null)?.closest?.("a, button");
      if (!el) return;
      const area = el.closest("[data-ga-area]")?.getAttribute("data-ga-area") ?? "content";

      const explicit = el.getAttribute("data-ga-event") as keyof EventMap | null;
      if (explicit && EXPLICIT_EVENTS.has(explicit)) {
        const params = parseJson<Record<string, unknown>>(el.getAttribute("data-ga-params")) ?? {};
        // find_agency_click: konum bulunduğu alandan (rezervasyon kutusu, kesinti bilgisi …)
        const full = explicit === "find_agency_click" ? { location: area, ...params } : params;
        track(explicit as "find_agency_click", full as EventMap["find_agency_click"]);
        return;
      }

      const share = el.closest("[data-share]");
      if (share) {
        const method = (el.getAttribute("data-network") ?? el.getAttribute("data-share-method")) as ShareMethod | null;
        const item = share.getAttribute("data-share-item");
        if (method && SHARE_METHODS.has(method) && item) track("share", { method, content_type: "tour", item_id: item });
        return;
      }

      const item = parseJson<AnalyticsItem>(el.getAttribute("data-ga-item"));
      if (item) {
        const list = parseJson<{ id: string; name: string }>(el.closest("[data-ga-list]")?.getAttribute("data-ga-list") ?? null) ?? { id: "other", name: "other" };
        const { price } = item;
        const index = Number(el.getAttribute("data-ga-index"));
        track("select_item", {
          item_list_id: list.id,
          item_list_name: list.name,
          ...(price !== undefined ? { currency: "EUR" as const } : {}),
          items: [{ ...item, ...(index > 0 ? { index } : {}), item_list_id: list.id, item_list_name: list.name }],
        });
        return;
      }

      const href = el instanceof HTMLAnchorElement ? el.getAttribute("href") : null;
      if (!href) return;
      const ev = classifyLink(href, { area, agencyPanelUrl });
      if (ev?.name === "contact_click") track("contact_click", ev.params);
      else if (ev?.name === "agency_login_click") track("agency_login_click", ev.params);
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("auxclick", onClick, true);
    };
  }, [agencyPanelUrl]);
}
